/**
 * Image URLs for the site (PageSpeed, 2026-10-04). Wikimedia images are served through our own
 * domain at /img/<width>/<file>: right-sized, cached for a year at the edge, no third-party
 * cookies. Widths are a fixed set so the edge cache stays small and nobody can request odd sizes.
 */
export const IMAGE_WIDTHS = [160, 330, 500, 800, 1200, 2000] as const;
export type ImageWidth = (typeof IMAGE_WIDTHS)[number];

const COMMONS = /^https:\/\/commons\.wikimedia\.org\/wiki\/Special:FilePath\/([^?]+)/;

/** Our proxied URL for a Commons image at `w` px wide; other URLs are returned unchanged. */
export function img(src: string, w: ImageWidth): string {
  const m = src.match(COMMONS);
  return m ? `/img/${w}/${m[1]}` : src;
}

/** srcset with 1x and 2x candidates around a base width. */
export function imgSrcSet(src: string, widths: ImageWidth[]): string | undefined {
  if (!COMMONS.test(src)) return undefined;
  return widths.map((w) => `${img(src, w)} ${w}w`).join(", ");
}

export function isImageWidth(n: number): n is ImageWidth {
  return (IMAGE_WIDTHS as readonly number[]).includes(n);
}

/**
 * Canonical key for an API GET path (sorted params, page=1 dropped). The edge worker embeds the
 * API responses it used for server rendering under these keys; the app reads them instead of
 * calling the API a second time (PageSpeed: LCP render delay).
 */
export function apiKey(path: string): string {
  const u = new URL(path, "https://x");
  const params = [...u.searchParams.entries()].filter(([k, v]) => !(k === "page" && v === "1")).sort(([a], [b]) => a.localeCompare(b));
  return u.pathname + (params.length ? "?" + new URLSearchParams(params).toString() : "");
}
